import { Box, Button, Flex, Heading, Spacer, Text } from "@chakra-ui/react";
import { Form, Formik } from "formik";
import { NavLink } from "react-router-dom";
import { NotFound } from "src/components/Feedback/NotFound";
import { AdminFormButtons } from "src/components/Layout/AdminFormButtons";
import { Breadcrumb } from "src/components/Navigation/Breadcrumb";
import { RequireUserPermissions } from "src/components/Permissions/RequireUserPermissions";
import { Glossary } from "src/components/Text/Glossary";
import * as breadcrumb from "src/services/breadcrumb";
import { validateWithZod } from "src/services/formValidations";
import { StudentForm } from "../components/Forms/StudentForm";
import { PossibleDuplicatesPanel } from "./components/PossibleDuplicatesPanel";
import { SelectedStudentChip } from "./components/SelectedStudentChip";
import { StudentGuardianSection } from "./components/StudentGuardianSection";
import { StudentProfileForm, StudentProfileValidator } from "./schemas";
import { useNewStudent } from "./useNewStudent";

export const NewStudent = () => {
  const {
    organization,
    organizationId,
    canCreateUser,
    initialValues,
    isSubmitting,
    onSubmit,
    cancelHref,
    returnsToForm,
    guardians,
    selectedExisting,
    onSelectExisting,
    onClearExisting,
  } = useNewStudent();

  // An open (unconfirmed) new parent/guardian sub-form must be added or cancelled
  // before the family can be saved.
  const hasOpenGuardianDraft = guardians.entries.some(
    (entry) => entry.kind === "new" && !entry.confirmed
  );

  if (!canCreateUser) {
    return <NotFound></NotFound>;
  }

  return (
    <RequireUserPermissions permission={"user:create"}>
      <Flex direction="column" minHeight="100%">
        <Breadcrumb
          items={breadcrumb.student.getBreadcrumbsForNew(organization)}
          mb={8}
        />
        <Formik<StudentProfileForm>
          initialValues={initialValues}
          onSubmit={onSubmit}
          // When linking to an existing student the profile form is hidden, so its
          // (now irrelevant) values must not block submit.
          validate={
            selectedExisting
              ? undefined
              : validateWithZod(StudentProfileValidator)
          }
          // Validate on mount so the Create button reflects validity immediately,
          // not only after the first edit or a submit attempt.
          validateOnMount
        >
          {({ isValid }) => (
            <Flex as={Form} direction="column" gap={6} flexGrow="1">
              <Heading as="h1" fontSize="2xl" fontWeight="600">
                <Glossary>Create student</Glossary>
              </Heading>
              <Flex
                direction={{ base: "column", lg: "row" }}
                gap={6}
                align="flex-start"
                width="100%"
              >
                <Flex direction="column" gap={6} flex="2" width="100%">
                  <Box
                    width="100%"
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="lg"
                    padding={5}
                  >
                    <Text fontSize="lg" fontWeight="700">
                      <Glossary>Student</Glossary>
                    </Text>
                    <Text fontSize="sm" color="gray.500" marginBottom={4}>
                      <Glossary>
                        {selectedExisting
                          ? "Using an existing student. New parent/guardians below will be linked to them."
                          : "Name and date of birth are filled from your search. The match panel checks them as you type."}
                      </Glossary>
                    </Text>
                    {selectedExisting ? (
                      <SelectedStudentChip
                        applicant={selectedExisting}
                        onClear={onClearExisting}
                      />
                    ) : (
                      <StudentForm requiredIndicators />
                    )}
                  </Box>
                  <Box
                    width="100%"
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="lg"
                    padding={5}
                  >
                    <Text fontSize="lg" fontWeight="700">
                      <Glossary>Parent/guardian</Glossary>
                    </Text>
                    <Text fontSize="sm" color="gray.500" marginBottom={4}>
                      <Glossary>
                        Parent/guardians are checked against contact methods.
                      </Glossary>
                    </Text>
                    <StudentGuardianSection
                      organizationId={organizationId}
                      entries={guardians.entries}
                      onAddExisting={guardians.addExisting}
                      onAddDraft={guardians.addDraft}
                      onUpdateDraft={guardians.updateDraft}
                      onConfirmDraft={guardians.confirmDraft}
                      onRemove={guardians.remove}
                      // Hide guardians already linked to the selected existing
                      // student so we never attempt a duplicate relationship.
                      excludeIds={selectedExisting?.guardians.map((g) => g.id)}
                    />
                  </Box>
                </Flex>
                {!selectedExisting && (
                  <Box flex="1" width="100%">
                    <PossibleDuplicatesPanel
                      organizationId={organizationId}
                      onUseStudent={onSelectExisting}
                    />
                  </Box>
                )}
              </Flex>
              <Spacer />
              <AdminFormButtons>
                <Button
                  as={NavLink}
                  to={cancelHref}
                  isLoading={organization.isLoading()}
                  variant="outline"
                  colorScheme="gray"
                >
                  Cancel
                </Button>
                <Spacer />
                <Button
                  type="submit"
                  marginLeft={4}
                  isLoading={isSubmitting}
                  // Linking to an existing student: the hidden form's validity is
                  // irrelevant, so gate only on having at least one parent/guardian.
                  // Creating a new student: gate on the form being valid.
                  isDisabled={
                    hasOpenGuardianDraft ||
                    (selectedExisting != null
                      ? guardians.entries.length === 0
                      : !isValid)
                  }
                >
                  <Glossary>
                    {returnsToForm
                      ? "Save and continue to form"
                      : "Save and create family"}
                  </Glossary>
                </Button>
              </AdminFormButtons>
            </Flex>
          )}
        </Formik>
      </Flex>
    </RequireUserPermissions>
  );
};
